import type {
  BulkUpsertEnvVarOverrides,
  CreateEnvVar,
  EnvVarOverride,
  ProcessEnvVar,
  UpdateEnvVar
} from '@stratumn/studio-shared';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { newError } from '@/services/errors/errors';
import { HTTPWrapper } from '../../utils/http';

const EMPTY_OVERRIDES: EnvVarOverride[] = [];

export class EnvVarStore {
  envVars: ProcessEnvVar[] = [];
  overrides: Map<string, EnvVarOverride[]> = new Map();
  loading = false;
  private loadedProcessId: string | null = null;

  private http = new HTTPWrapper('process');

  constructor() {
    makeObservable(this, {
      envVars: observable,
      overrides: observable,
      loading: observable,
      fetchEnvVars: action.bound,
      createEnvVar: action.bound,
      updateEnvVar: action.bound,
      deleteEnvVar: action.bound,
      fetchOverrides: action.bound,
      bulkUpsertOverrides: action.bound
    });
  }

  async fetchEnvVars(processId: string) {
    // Only wipe stale data when switching processes. Refetching the same
    // process should keep the current data visible until the new response
    // arrives — otherwise the "Variables" menu item flickers out and back
    // in (it depends on `envVars.some(v => v.shared)`).
    const isDifferentProcess = this.loadedProcessId !== processId;
    runInAction(() => {
      if (isDifferentProcess) {
        this.envVars = [];
      }
      this.loading = true;
    });
    try {
      const result = await this.http.get<ProcessEnvVar[]>(
        `/${processId}/env-vars`
      );
      runInAction(() => {
        this.envVars = result ?? [];
        this.loadedProcessId = processId;
      });
    } catch (error) {
      throw newError('EnvVar-fetchAll', error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async createEnvVar(processId: string, dto: CreateEnvVar) {
    try {
      const result = await this.http.post<ProcessEnvVar, CreateEnvVar>(
        `/${processId}/env-vars`,
        dto
      );
      if (result) {
        runInAction(() => {
          this.envVars.push(result);
        });
      }
      return result;
    } catch (error) {
      throw newError('EnvVar-create', error);
    }
  }

  async updateEnvVar(processId: string, varId: string, dto: UpdateEnvVar) {
    try {
      const result = await this.http.patch<ProcessEnvVar, UpdateEnvVar>(
        `/${processId}/env-vars/${varId}`,
        dto
      );
      if (result) {
        runInAction(() => {
          const idx = this.envVars.findIndex(v => v.id === varId);
          if (idx >= 0) {
            this.envVars[idx] = result;
          }
        });
      }
      return result;
    } catch (error) {
      throw newError('EnvVar-update', error);
    }
  }

  async deleteEnvVar(processId: string, varId: string) {
    try {
      await this.http.delete(`/${processId}/env-vars/${varId}`);
      runInAction(() => {
        this.envVars = this.envVars.filter(v => v.id !== varId);
      });
    } catch (error) {
      throw newError('EnvVar-delete', error);
    }
  }

  async fetchOverrides(processId: string, workspaceId: string) {
    try {
      const result = await this.http.get<EnvVarOverride[]>(
        `/${processId}/workspaces/${workspaceId}/env-overrides`
      );
      runInAction(() => {
        this.overrides.set(workspaceId, result ?? []);
      });
    } catch (error) {
      throw newError('EnvVar-fetchOverrides', error);
    }
  }

  async bulkUpsertOverrides(
    processId: string,
    workspaceId: string,
    dto: BulkUpsertEnvVarOverrides
  ) {
    try {
      const result = await this.http.put<
        EnvVarOverride[],
        BulkUpsertEnvVarOverrides
      >(`/${processId}/workspaces/${workspaceId}/env-overrides`, dto);
      runInAction(() => {
        this.overrides.set(workspaceId, result ?? []);
      });
      return result;
    } catch (error) {
      throw newError('EnvVar-upsertOverrides', error);
    }
  }

  getOverridesForWorkspace(workspaceId: string): EnvVarOverride[] {
    return this.overrides.get(workspaceId) ?? EMPTY_OVERRIDES;
  }
}
